import React, { useState } from "react";
import { PiPencilSimpleLineBold, PiEyeBold, PiXBold, PiCodeBold } from "react-icons/pi";
import "./html-preview-editor.css";

/**
 * HtmlPreviewEditor
 *
 * Wraps a CKEditor instance inside the AI campaign-template steps (Email /
 * Landing / Awareness). By default it shows a *rendered* preview of the page in
 * a sandboxed iframe — the raw CKEditor stays hidden until the user clicks
 * "Open in CKEditor". A "Close editor" button collapses it back to the preview.
 *
 * The optional `promptBar` node (the "Edit with prompt" bar) renders directly
 * beneath the page so it sits flush with the content instead of floating at the
 * bottom of the panel.
 *
 * Props:
 *   html       {string}     current HTML to render in the preview
 *   label      {string}     human label, e.g. "Email Template"
 *   promptBar  {ReactNode}  optional "Edit with prompt" bar
 *   children   {ReactNode}  the CKEditor instance (only mounted while editing)
 */
export default function HtmlPreviewEditor({ html, label = "Page", promptBar, children }) {
    const [editing, setEditing] = useState(false);

    const hasHtml = typeof html === "string" && html.trim().length > 0;
    const previewDoc = hasHtml
        ? html
        : `<!doctype html><html><body style="margin:0;font-family:'Inter',-apple-system,Segoe UI,sans-serif;display:flex;align-items:center;justify-content:center;height:100%;color:#98a2b3;font-size:13px;">Nothing to preview yet.</body></html>`;

    return (
        <div className={`hpe-wrap ${editing ? "is-editing" : ""}`}>
            <div className="hpe-toolbar">
                <span className="hpe-toolbar-title">
                    {editing ? <PiCodeBold className="hpe-toolbar-icon" /> : <PiEyeBold className="hpe-toolbar-icon" />}
                    {editing ? `Editing ${label}` : `${label} preview`}
                </span>

                {editing ? (
                    <button
                        type="button"
                        className="hpe-btn hpe-btn--close"
                        onClick={() => setEditing(false)}
                        title="Close editor and return to preview"
                    >
                        <PiXBold className="hpe-btn-icon" />
                        Close editor
                    </button>
                ) : (
                    <button
                        type="button"
                        className="hpe-btn hpe-btn--open"
                        onClick={() => setEditing(true)}
                        title="Edit the raw HTML in CKEditor"
                    >
                        <PiPencilSimpleLineBold className="hpe-btn-icon" />
                        Open in CKEditor
                    </button>
                )}
            </div>

            {editing ? (
                <div className="hpe-editor">{children}</div>
            ) : (
                <div className="hpe-preview">
                    <iframe
                        title={`${label} preview`}
                        className="hpe-preview-frame"
                        srcDoc={previewDoc}
                        sandbox=""
                    />
                </div>
            )}

            {promptBar ? <div className="hpe-prompt-slot">{promptBar}</div> : null}
        </div>
    );
}
